import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus,
  Megaphone,
  Calendar,
  Users,
  Euro,
  Play,
  Pause,
  CheckCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-slate-100 text-slate-800', icon: Pause },
  ativa: { label: 'Ativa', color: 'bg-emerald-100 text-emerald-800', icon: Play },
  pausada: { label: 'Pausada', color: 'bg-amber-100 text-amber-800', icon: Pause },
  concluida: { label: 'Concluída', color: 'bg-blue-100 text-blue-800', icon: CheckCircle }
};

export default function CampaignsPage() {
  const { api } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    status: 'rascunho',
    start_date: '',
    end_date: '',
    budget: '',
    target_contacts: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [campaignsRes, contactsRes] = await Promise.all([
        api().get('/campaigns'),
        api().get('/contacts')
      ]);
      setCampaigns(campaignsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...form,
      budget: parseFloat(form.budget) || 0
    };
    
    try {
      if (editingCampaign) {
        await api().put(`/campaigns/${editingCampaign.id}`, data);
        toast.success('Campanha atualizada!');
      } else {
        await api().post('/campaigns', data);
        toast.success('Campanha criada!');
      }
      
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao guardar campanha');
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setForm({
      name: campaign.name || '',
      description: campaign.description || '',
      status: campaign.status || 'rascunho',
      start_date: campaign.start_date || '',
      end_date: campaign.end_date || '',
      budget: campaign.budget?.toString() || '',
      target_contacts: campaign.target_contacts || []
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      status: 'rascunho',
      start_date: '',
      end_date: '',
      budget: '',
      target_contacts: []
    });
    setEditingCampaign(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {campaigns.length} campanhas • {campaigns.filter(c => c.status === 'ativa').length} ativas
          </p>
        </div>
        <Button 
          className="bg-indigo-800 hover:bg-indigo-900"
          onClick={() => { resetForm(); setShowModal(true); }}
          data-testid="new-campaign-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Campanha
        </Button>
      </div>

      {/* Campaigns Grid */}
      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="p-16 text-center">
            <Megaphone className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold font-[Manrope]">Nenhuma campanha</h3>
            <p className="text-muted-foreground mt-1">Crie uma campanha para organizar as suas ações de vendas</p>
            <Button 
              className="mt-4 bg-indigo-800 hover:bg-indigo-900"
              onClick={() => { resetForm(); setShowModal(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Criar Campanha
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => {
            const statusConfig = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.rascunho;
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card 
                key={campaign.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleEdit(campaign)}
                data-testid={`campaign-card-${campaign.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Megaphone className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="font-medium font-[Manrope]">{campaign.name}</h4>
                        <Badge className={`text-xs mt-1 ${statusConfig.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {campaign.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {campaign.description}
                    </p>
                  )}
                  
                  <div className="space-y-2 text-sm">
                    {(campaign.start_date || campaign.end_date) && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {campaign.start_date && format(new Date(campaign.start_date), "d MMM", { locale: pt })}
                          {campaign.start_date && campaign.end_date && ' - '}
                          {campaign.end_date && format(new Date(campaign.end_date), "d MMM yyyy", { locale: pt })}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4" />
                      <span>{campaign.target_contacts?.length || 0} contactos</span>
                    </div>
                    
                    {campaign.budget > 0 && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Euro className="h-4 w-4" />
                        <span className="tabular-nums">{formatCurrency(campaign.budget)}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editingCampaign ? 'Editar Campanha' : 'Nova Campanha'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="campaign-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="campaign-description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="campaign-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget">Orçamento (€)</Label>
                  <Input
                    id="budget"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                    data-testid="campaign-budget-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Data Início</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    data-testid="campaign-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Data Fim</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    data-testid="campaign-end-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-indigo-800 hover:bg-indigo-900" data-testid="save-campaign-btn">
                {editingCampaign ? 'Guardar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
